/**
 * StatsTabButtonView - Presentation for stats tab button
 *
 * Displays a styled tab button for statistics navigation
 * @remarks This is a View component - contains no business logic or hooks
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Props for StatsTabButtonView component
 */
interface StatsTabButtonViewProps {
  /** Tab label text */
  readonly label: string;
  /** Whether tab is currently active */
  readonly isActive: boolean;
  /** Whether dark mode is enabled */
  readonly isDark: boolean;
  /** Callback when button is pressed */
  readonly onPress: () => void;
  /** Optional testID for E2E testing */
  readonly testID?: string;
}

/**
 * StatsTabButtonView renders a styled tab button
 * @param props - View props
 * @param props.label - Text label for the button
 * @param props.isActive - Whether tab is currently selected
 * @param props.onPress - Callback when button is pressed
 * @param props.testID - Optional testID for E2E testing
 */
const StatsTabButtonView = ({
  label,
  isActive,
  onPress,
  testID,
}: StatsTabButtonViewProps) => (
  <Pressable testID={testID} onPress={onPress}>
    <Box
      className={`rounded-full px-3 py-1 ${
        isActive ? "bg-accent-primary" : "bg-surface-muted"
      }`}
    >
      <Text
        variant="caption-strong"
        className={
          isActive ? "text-content-on-accent" : "text-content-secondary"
        }
      >
        {label}
      </Text>
    </Box>
  </Pressable>
);

StatsTabButtonView.displayName = "StatsTabButtonView";

export default memo(StatsTabButtonView);
